import { useRef, useEffect, useCallback } from 'react';
import { getPrimaryPlaceType, mapPlaceTypeToCategory, getSubcategoryFromType } from '../services/googleMaps';
import { logApiCall } from '../services/googleMapsLogger';

/**
 * Monkey-patch attachShadow to force open mode for gmp-place-autocomplete
 * This allows us to inject custom styles into Google's closed shadow DOM
 * Styles are only applied in dark mode to avoid affecting light mode users
 */
function setupShadowDOMStyling() {
  if (window.gmpShadowPatched) return;

  const originalAttachShadow = Element.prototype.attachShadow;

  Element.prototype.attachShadow = function(init) {
    if (this.localName === 'gmp-place-autocomplete') {
      // Force shadow DOM to be open so we can style it
      const shadow = originalAttachShadow.call(this, {
        ...init,
        mode: 'open'
      });

      // Only inject dark mode styles if user is in dark mode
      // Check for body.dark-mode class which is set by the app's theme system
      const isDarkMode = document.body.classList.contains('dark-mode');

      if (isDarkMode) {
        // Inject dark mode compatible styles - make text visible on dark background
        const style = document.createElement('style');
        style.textContent = `
          /* Base text - light gray for non-matched portions */
          * {
            color: #a0a0a0 !important;
          }

          /* Matched/highlighted text - bright white and bold to stand out */
          b, strong, [class*="match"], .matched {
            color: #ffffff !important;
            font-weight: 700 !important;
          }

          /* Secondary text (address) - dimmer gray */
          [class*="secondary"],
          [class*="description"] {
            color: #808080 !important;
          }

          /* Make location icons visible - force fill color */
          svg {
            fill: #808080 !important;
          }
          svg * {
            fill: #808080 !important;
          }

          /* Override Google's hover/selected light background with dark */
          [class*="selected"],
          [class*="hover"],
          [class*="active"],
          [class*="focused"],
          [aria-selected="true"],
          :hover {
            background: #1a1a1a !important;
            background-color: #1a1a1a !important;
          }

          /* Override any inline background styles */
          [style*="background"] {
            background: #1a1a1a !important;
            background-color: #1a1a1a !important;
          }
        `;
        shadow.appendChild(style);
      }

      return shadow;
    }
    return originalAttachShadow.call(this, init);
  };

  window.gmpShadowPatched = true;
}

// Apply the patch immediately when this module loads
setupShadowDOMStyling();

/**
 * Custom hook for Google Places Autocomplete using the new PlaceAutocompleteElement API
 * (Replaces deprecated google.maps.places.Autocomplete as of March 2025)
 *
 * Requires "Places API (New)" to be enabled in Google Cloud Console
 *
 * Uses Autocomplete (New) which costs $3/1000 for session-based billing
 * Place Details (New) Essentials tier is used for the selected place ($5/1000)
 */
export function usePlaceAutocomplete({
  isOpen,
  autocompleteKey,
  onPlaceSelected,
}) {
  const containerRef = useRef(null);
  const autocompleteElementRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    // Check if PlaceAutocompleteElement is available
    const PlaceAutocompleteElement = window.google?.maps?.places?.PlaceAutocompleteElement;
    if (!PlaceAutocompleteElement) {
      console.warn('PlaceAutocompleteElement not available');
      return;
    }

    // Clean up any existing autocomplete element
    if (autocompleteElementRef.current) {
      autocompleteElementRef.current.remove();
      autocompleteElementRef.current = null;
    }

    // Clear container safely (avoid innerHTML for security)
    while (containerRef.current.firstChild) {
      containerRef.current.removeChild(containerRef.current.firstChild);
    }

    // Create the new PlaceAutocompleteElement
    // Note: The shadow DOM patch above will inject light mode styles automatically
    const autocompleteElement = new PlaceAutocompleteElement({});
    autocompleteElementRef.current = autocompleteElement;

    // Append to container
    containerRef.current.appendChild(autocompleteElement);

    // Handle place selection using gmp-select event
    const handlePlaceSelect = async (event) => {
      try {
        // The new API provides placePrediction in the event
        const placePrediction = event.placePrediction || event.place;

        if (!placePrediction) {
          console.error('No place prediction in event');
          return;
        }

        // Convert prediction to Place object (new API)
        const place = placePrediction.toPlace ? placePrediction.toPlace() : placePrediction;

        // Fetch place details using Places API (New) - Essentials tier fields only
        await place.fetchFields({
          fields: [
            'displayName',
            'formattedAddress',
            'addressComponents',
            'location',
            'types',
            'id',
            'googleMapsURI',
          ]
        });

        // Log the API call
        logApiCall({
          requestType: 'Autocomplete-Session',
          placeId: place.id,
          fields: ['displayName', 'formattedAddress', 'addressComponents', 'location', 'types', 'googleMapsURI'],
          extra: { caller: 'AddPlaceModal-Autocomplete', placeName: place.displayName }
        });

        const primaryPlaceType = getPrimaryPlaceType(place.types || []);
        const mappedCategory = mapPlaceTypeToCategory(primaryPlaceType);
        const subcategory = getSubcategoryFromType(primaryPlaceType);

        // Build place data from the new API response format
        const placeData = {
          name: place.displayName || '',
          address: place.formattedAddress || '',
          lat: place.location?.lat() || 0,
          lng: place.location?.lng() || 0,
          placeType: primaryPlaceType || '',
          category: mappedCategory,
          subcategory: subcategory,
          addressComponents: place.addressComponents ?
            place.addressComponents.map(comp => ({
              long_name: comp.longText,
              short_name: comp.shortText,
              types: comp.types
            })) : null,
          placeId: place.id || null,
          googleMapsUrl: place.googleMapsURI || null,
        };

        onPlaceSelected(placeData);
      } catch (error) {
        console.error('Failed to fetch place details:', error);
      }
    };

    // Listen for gmp-select event (the correct event for PlaceAutocompleteElement)
    autocompleteElement.addEventListener('gmp-select', handlePlaceSelect);

    // Focus the input after mounting
    requestAnimationFrame(() => {
      const input = autocompleteElement.querySelector('input') ||
                    autocompleteElement.shadowRoot?.querySelector('input');
      if (input) {
        input.focus();
      }
    });

    return () => {
      autocompleteElement.removeEventListener('gmp-select', handlePlaceSelect);
      if (autocompleteElementRef.current) {
        autocompleteElementRef.current.remove();
        autocompleteElementRef.current = null;
      }
    };
  }, [isOpen, autocompleteKey, onPlaceSelected]);

  const clearInput = useCallback(() => {
    if (autocompleteElementRef.current) {
      const input = autocompleteElementRef.current.querySelector('input') ||
                    autocompleteElementRef.current.shadowRoot?.querySelector('input');
      if (input) {
        input.value = '';
      }
    }
  }, []);

  const showDropdown = useCallback(() => {
    // PlaceAutocompleteElement handles its own dropdown
  }, []);

  const focusInput = useCallback(() => {
    if (autocompleteElementRef.current) {
      const input = autocompleteElementRef.current.querySelector('input') ||
                    autocompleteElementRef.current.shadowRoot?.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }, []);

  return {
    containerRef,
    clearInput,
    showDropdown,
    focusInput,
  };
}
